import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { Modal, ConfirmModal } from '../../components/common/Modal';
import {
  useExtraCosts,
  useCreateExtraCost,
  useUpdateExtraCost,
  useDeleteExtraCost,
  useUploadExtraCostFile,
  useExtraCostOverview,
  useApproveExtraCosts,
} from '../../hooks/useEmployeeExtraCosts';
import { useExtraCostTypes } from '../../hooks/useEmployeeExtraCostTypes';
import { getAdminUsers, type AdminUser } from '../../services/api/admin';
import type { ExtraCost, CreateExtraCostInput, UpdateExtraCostInput } from '../../services/api/employeeExtraCosts';
import { formatDisplayDate } from '../../utils/format';
import { DateField } from '../../components/ui/DateField';
import { LEGACY_CZ_NUMBER_LOCALE, numberFormatter } from '../../utils/localeFormat';

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return numberFormatter({
    style: 'currency',
    currency: 'CZK',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }, LEGACY_CZ_NUMBER_LOCALE).format(value);
}

function formatDate(dateStr: string | null | undefined): string {
  // MKS-351 — dd.mm.yyyy from the shared util.
  return formatDisplayDate(dateStr);
}

function userName(user: ExtraCost['user']): string {
  if (!user) return '-';
  if (user.firstName || user.lastName) {
    return [user.firstName, user.lastName].filter(Boolean).join(' ');
  }
  return user.email;
}

type StatusValue = 'new' | 'pending' | 'partial' | 'applied';

function StatusBadge({ status }: { status: StatusValue }) {
  const styles: Record<StatusValue, string> = {
    new: 'bg-blue-50 text-blue-700 border-blue-300',
    pending: 'bg-gray-100 text-gray-700 border-gray-300',
    partial: 'bg-yellow-50 text-yellow-700 border-yellow-300',
    applied: 'bg-green-50 text-green-700 border-green-300',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
}

// ===== Form Modal =====

function ExtraCostFormModal({
  cost,
  users,
  onClose,
}: {
  cost: ExtraCost | null;
  users: AdminUser[];
  onClose: () => void;
}) {
  const createMutation = useCreateExtraCost();
  const updateMutation = useUpdateExtraCost();
  const { data: costTypes = [] } = useExtraCostTypes();
  const isEdit = !!cost;

  const [form, setForm] = useState({
    userId: cost?.userId?.toString() || '',
    description: cost?.description || '',
    amount: cost?.amount?.toString() || '',
    costDate: cost?.costDate || '',
    typeId: cost?.typeId?.toString() || '',
    recurring: cost?.recurring ?? false,
    frequencyMonths: cost?.frequencyMonths?.toString() || '',
  });
  const [error, setError] = useState<string | null>(null);

  // Pre-fill recurring settings when type is selected
  const handleTypeChange = (typeIdStr: string) => {
    const selectedType = costTypes.find((t) => t.id === parseInt(typeIdStr, 10));
    if (selectedType && !isEdit) {
      setForm((prev) => ({
        ...prev,
        typeId: typeIdStr,
        recurring: selectedType.recurring,
        frequencyMonths: selectedType.frequencyMonths ? String(selectedType.frequencyMonths) : '',
      }));
    } else {
      setForm((prev) => ({ ...prev, typeId: typeIdStr }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const typeIdValue = form.typeId ? parseInt(form.typeId, 10) : null;
      const freqValue = form.recurring && form.frequencyMonths ? parseInt(form.frequencyMonths, 10) : null;
      if (isEdit) {
        const input: UpdateExtraCostInput = {};
        if (form.description !== cost.description) input.description = form.description;
        if (form.amount !== cost.amount.toString()) input.amount = parseFloat(form.amount);
        if (form.costDate !== cost.costDate) input.costDate = form.costDate;
        if (typeIdValue !== cost.typeId) input.typeId = typeIdValue;
        if (form.recurring !== cost.recurring) input.recurring = form.recurring;
        if (freqValue !== cost.frequencyMonths) input.frequencyMonths = freqValue;
        await updateMutation.mutateAsync({ id: cost.id, input });
      } else {
        const input: CreateExtraCostInput = {
          userId: parseInt(form.userId, 10),
          description: form.description,
          amount: parseFloat(form.amount),
          costDate: form.costDate,
          typeId: typeIdValue,
          recurring: form.recurring,
          frequencyMonths: freqValue,
        };
        await createMutation.mutateAsync(input);
      }
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to save');
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const inputClass = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  const activeUsers = users
    .filter((u) => !u.deletedAt)
    .sort((a, b) => {
      const aLast = (a.lastName || '').toLowerCase();
      const bLast = (b.lastName || '').toLowerCase();
      if (aLast !== bLast) return aLast.localeCompare(bLast);
      const aFirst = (a.firstName || '').toLowerCase();
      const bFirst = (b.firstName || '').toLowerCase();
      return aFirst.localeCompare(bFirst);
    });

  return (
    <Modal isOpen onClose={onClose} title={isEdit ? 'Edit Extra Cost' : 'New Extra Cost'} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <p className="text-sm text-red-600">{error}</p>}

        {!isEdit && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee <span className="text-red-500">*</span>
            </label>
            <select
              className={inputClass}
              value={form.userId}
              onChange={(e) => setForm({ ...form, userId: e.target.value })}
              required
            >
              <option value="">Select employee...</option>
              {activeUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.lastName || u.firstName
                    ? [u.lastName, u.firstName].filter(Boolean).join(' ')
                    : u.email}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type
          </label>
          <select
            className={inputClass}
            value={form.typeId}
            onChange={(e) => handleTypeChange(e.target.value)}
          >
            <option value="">No type</option>
            {costTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}{t.recurring ? ' (recurring)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            className={inputClass}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            required
            placeholder="e.g. Mobile phone bill"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount (CZK) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              className={inputClass}
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              required
            />
            {isEdit && cost.appliedAmount > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Min: {formatCurrency(cost.appliedAmount)} (already applied)
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cost Date <span className="text-red-500">*</span>
            </label>
            <DateField
              className={inputClass}
              value={form.costDate}
              onChange={(nextDate) => setForm({ ...form, costDate: nextDate })}
              required
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.recurring}
              onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
              className="rounded border-gray-300"
            />
            Recurring cost
          </label>
          {form.recurring && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700">Every</label>
              <input
                type="number"
                min="1"
                className="w-20 rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                value={form.frequencyMonths}
                onChange={(e) => setForm({ ...form, frequencyMonths: e.target.value })}
                placeholder="1"
              />
              <span className="text-sm text-gray-700">month(s)</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" isLoading={isPending}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ===== Main Page =====

export default function EmployeeExtraCostPage() {
  const navigate = useNavigate();
  const deleteMutation = useDeleteExtraCost();
  const uploadMutation = useUploadExtraCostFile();
  const approveMutation = useApproveExtraCosts();

  // View toggle
  const [view, setView] = useState<'list' | 'overview'>('list');

  // Filters
  const [filterUserId, setFilterUserId] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterTypeId, setFilterTypeId] = useState<string>('');

  const filters = {
    ...(filterUserId ? { userId: parseInt(filterUserId, 10) } : {}),
    ...(filterStatus ? { status: filterStatus } : {}),
    ...(filterTypeId ? { typeId: parseInt(filterTypeId, 10) } : {}),
  };

  const { data: costs, isLoading } = useExtraCosts(
    Object.keys(filters).length > 0 ? filters : undefined
  );
  const { data: costTypes = [] } = useExtraCostTypes();
  const { data: users = [] } = useQuery({
    queryKey: ['adminUsers'],
    queryFn: () => getAdminUsers(),
  });

  // Modal state
  const [editingCost, setEditingCost] = useState<ExtraCost | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Bulk approve state
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const newCosts = costs?.filter((c) => c.state === 'new') ?? [];
  const toggleSelected = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllNew = () => {
    if (selectedIds.size === newCosts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(newCosts.map((c) => c.id)));
    }
  };
  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    await approveMutation.mutateAsync([...selectedIds]);
    setSelectedIds(new Set());
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    await deleteMutation.mutateAsync(deleteId);
    setDeleteId(null);
  };

  const handleFileUpload = async (costId: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      await uploadMutation.mutateAsync({ id: costId, file });
    };
    input.click();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/admin')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-1"
        >
          &larr; Back to Admin
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Employee Extra Costs</h1>
          <Button size="sm" onClick={() => setShowCreateModal(true)}>
            New Extra Cost
          </Button>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            view === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setView('list')}
        >
          All Costs
        </button>
        <button
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            view === 'overview' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setView('overview')}
        >
          Overview
        </button>
      </div>

      {view === 'overview' ? (
        <OverviewView onSelectUser={(userId) => { setFilterUserId(String(userId)); setView('list'); }} />
      ) : (
        <>
          {/* Filters */}
          <div className="flex gap-3 items-center">
            <select
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={filterUserId}
              onChange={(e) => setFilterUserId(e.target.value)}
            >
              <option value="">All employees</option>
              {users
                .filter((u) => !u.deletedAt)
                .sort((a, b) => {
                  const aLast = (a.lastName || '').toLowerCase();
                  const bLast = (b.lastName || '').toLowerCase();
                  if (aLast !== bLast) return aLast.localeCompare(bLast);
                  return (a.firstName || '').toLowerCase().localeCompare((b.firstName || '').toLowerCase());
                })
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.lastName || u.firstName
                      ? [u.lastName, u.firstName].filter(Boolean).join(' ')
                      : u.email}
                  </option>
                ))}
            </select>

            <select
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="new">New</option>
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
              <option value="applied">Applied</option>
            </select>

            <select
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={filterTypeId}
              onChange={(e) => setFilterTypeId(e.target.value)}
            >
              <option value="">All types</option>
              {costTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>

            {selectedIds.size > 0 && (
              <Button
                size="sm"
                onClick={handleBulkApprove}
                isLoading={approveMutation.isPending}
              >
                Approve selected ({selectedIds.size})
              </Button>
            )}
          </div>

          {/* Table */}
      {!costs || costs.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-200 p-12 text-center">
          <p className="text-gray-500">No extra costs found.</p>
          <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
            Add First Extra Cost
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {newCosts.length > 0 && (
                  <th className="px-3 py-3 w-8">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300"
                      checked={selectedIds.size > 0 && selectedIds.size === newCosts.length}
                      onChange={toggleAllNew}
                    />
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Employee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Description
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Cost Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Applied
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Remaining
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {costs.map((cost) => (
                <tr
                  key={cost.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/admin/employee-extra-costs/${cost.id}`)}
                >
                  {newCosts.length > 0 && (
                    <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                      {cost.state === 'new' && (
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={selectedIds.has(cost.id)}
                          onChange={() => toggleSelected(cost.id)}
                        />
                      )}
                    </td>
                  )}
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">
                    {userName(cost.user)}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-500">
                    {cost.typeName || '-'}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-900">
                    <div className="flex items-center gap-2">
                      {cost.description}
                      {cost.recurring && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600 border border-blue-200" title={`Every ${cost.frequencyMonths} month(s)`}>
                          {cost.frequencyMonths}mo
                        </span>
                      )}
                      {cost.hasFile && (
                        <span className="text-gray-400" title="File attached">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-900 text-right">
                    {formatCurrency(cost.amount)}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-500">
                    {formatDate(cost.costDate)}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-900 text-right">
                    {formatCurrency(cost.appliedAmount)}
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-900 text-right">
                    {formatCurrency(cost.remainingAmount)}
                  </td>
                  <td className="px-6 py-3 text-center">
                    <StatusBadge status={cost.status} />
                  </td>
                  <td className="px-6 py-3 text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleFileUpload(cost.id)}
                      title="Upload file"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingCost(cost)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteId(cost.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <ExtraCostFormModal
          cost={null}
          users={users}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Edit Modal */}
      {editingCost && (
        <ExtraCostFormModal
          cost={editingCost}
          users={users}
          onClose={() => setEditingCost(null)}
        />
      )}

      {/* Delete Confirm */}
      <ConfirmModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Extra Cost"
        message="Are you sure you want to delete this extra cost? This cannot be undone."
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
        </>
      )}
    </div>
  );
}

// ===== Overview View =====

function OverviewView({ onSelectUser }: { onSelectUser: (userId: number) => void }) {
  const { data: overview, isLoading } = useExtraCostOverview();

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (!overview || overview.length === 0) {
    return (
      <div className="rounded-lg border-2 border-dashed border-gray-200 p-12 text-center">
        <p className="text-gray-500">No extra costs found for any employees.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Costs</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Applied</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Remaining</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {overview.map((item) => (
            <tr
              key={item.userId}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => onSelectUser(item.userId)}
            >
              <td className="px-6 py-3 text-sm font-medium text-gray-900">{item.userName}</td>
              <td className="px-6 py-3 text-sm text-gray-900 text-right">{formatCurrency(item.totalCosts)}</td>
              <td className="px-6 py-3 text-sm text-gray-900 text-right">{formatCurrency(item.totalApplied)}</td>
              <td className="px-6 py-3 text-sm text-gray-900 text-right font-medium">
                {formatCurrency(item.totalRemaining)}
              </td>
              <td className="px-6 py-3 text-sm text-gray-500 text-right">{item.costCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
